/**
 * PG / Hostel / Co-living solution page — /industry/pg-hostel-management-software
 *
 * High-converting sales page for PG, hostel and co-living operators across India.
 * Built specifically for residential long-stay operations: beds not rooms,
 * months not nights, tenants not transient guests.
 *
 * Product reality this page is written against (poc.axoix.io):
 *   - PG is a dedicated SUBTYPE of the `hotel` vertical (`vertical_subtype: 'pg'`).
 *   - CTAs send ?vertical=pg&from=pg to trigger the specialized PG onboarding wizard.
 *   - 69+ PG endpoints ship: bed matrix, rent runs, sub-metering, deposit settlements,
 *     bank statement matching, mess menu/orders, and tenant self-service portal.
 *   - WhatsApp bed booking & resident chat live; automated WhatsApp notification
 *     templates rolling out with Meta app review.
 */
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header/Header";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import SEO from "../../components/SEO/SEO";
import { getDemosByVertical } from "../../api/demoTenants";
import PricingSection from "../../components/ai-chatbot-components/PricingSection/PricingSection";
import { rememberVerticalIntent } from "../../utils/verticalIntent";

// Background images
import cardBg from "../../images/bg/pricing-bg01.png";
import serviceBg from "../../images/bg/pricing-bg.png";
import subLeftIcon from "../../images/icon/sub-left-icon.png";
import subRightIcon from "../../images/icon/sub-right-icon.png";

const PAGE_PATH = "/industry/pg-hostel-management-software";

// Index-aligned with pgPms.featureTitles / pgPms.featureDescs (15 entries).
const FEATURE_ICONS = [
  "fa-bed",
  "fa-file-invoice-rupee-sign",
  "fa-bolt",
  "fa-vault",
  "fa-calendar-xmark",
  "fa-id-card",
  "fa-building-columns",
  "fa-utensils",
  "fa-globe",
  "fa-book",
  "fa-chart-line",
  "fa-user-shield",
  "fa-headset",
  "fa-mobile-screen-button",
  "fa-door-open",
];

// Index-aligned with pgPms.articleTitles (16 entries). Slugs verified published.
const RELATED_ARTICLES = [
  "pg-hostel-management-software-india-2026",
  "pg-rent-billing-security-deposits",
  "pg-electricity-water-meter-billing",
  "pg-self-service-bed-booking-online-payment",
  "pg-tenant-self-service-portal",
  "pg-tenant-portal-access-approval",
  "pg-automated-rent-notice-reminders",
  "pg-analytics-occupancy-revenue-trends",
  "girls-pg-safety-compliance-software",
  "pg-tenant-inbox-whatsapp-complaints",
  "pg-maintenance-request-tracking",
  "pg-housekeeping-turnover-tracking",
  "pg-tenant-document-uploads",
  "pg-accounting-gst-honest-limits",
  "pg-bill-correction-accounting-reversal",
  "hotel-pg-hostel-fnb-one-system",
];

const splitPipe = (value: string): string[] =>
  value ? value.split("|").map((s) => s.trim()).filter(Boolean) : [];

// Core platform modules available to all tenants
const CORE_MODULES = [
  { icon: "fa-calculator", nameKey: "industryDetail.coreModules.accounting" },
  { icon: "fa-users", nameKey: "industryDetail.coreModules.hr" },
  { icon: "fa-handshake", nameKey: "industryDetail.coreModules.crm" },
  { icon: "fa-qrcode", nameKey: "industryDetail.coreModules.upi" },
  { icon: "fa-chart-line", nameKey: "industryDetail.coreModules.analytics" },
  { icon: "fa-comments", nameKey: "industryDetail.coreModules.connect" },
  { icon: "fa-shield-alt", nameKey: "industryDetail.coreModules.compliance" },
  { icon: "fa-stamp", nameKey: "industryDetail.coreModules.esign" },
];

const pageStyles = `
  /* Hero Section */
  .pg-hero-section {
    position: relative;
    padding: 140px 0 80px;
    background: radial-gradient(circle at 50% 20%, rgba(0, 255, 151, 0.08) 0%, transparent 60%);
    overflow: hidden;
  }
  .pg-badge-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(0, 255, 151, 0.1);
    border: 1px solid rgba(0, 255, 151, 0.35);
    color: var(--color-primary);
    padding: 7px 18px;
    border-radius: 30px;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 22px;
    box-shadow: 0 4px 15px rgba(0, 255, 151, 0.15);
  }
  .pg-hero-title {
    font-size: 3rem;
    font-weight: 800;
    line-height: 1.2;
    margin-bottom: 20px;
    color: #ffffff;
    letter-spacing: -0.5px;
  }
  @media (max-width: 991px) {
    .pg-hero-title { font-size: 2.3rem; }
  }
  @media (max-width: 575px) {
    .pg-hero-title { font-size: 1.85rem; }
  }
  .pg-hero-desc {
    font-size: 1.15rem;
    line-height: 1.75;
    opacity: 0.85;
    max-width: 760px;
    margin: 0 auto 32px;
  }
  .pg-hero-trust-bullets {
    display: flex;
    justify-content: center;
    gap: 24px;
    flex-wrap: wrap;
    font-size: 13px;
    opacity: 0.8;
    margin-top: 20px;
  }
  .pg-hero-trust-bullets span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  /* Uniform CTA button styles (eliminates arrow rolling animation and broken corner flaps) */
  .pg-hero-btn-group {
    display: flex;
    gap: 14px;
    align-items: center;
    flex-wrap: wrap;
  }
  .pg-cta-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    border-radius: 30px !important;
    padding: 15px 30px !important;
    text-decoration: none !important;
    transition: all 0.25s ease;
    cursor: pointer;
    box-sizing: border-box;
    white-space: nowrap;
  }
  .pg-cta-btn-primary {
    background: var(--color-primary) !important;
    color: #060e1a !important;
    border: 1px solid var(--color-primary) !important;
    box-shadow: 0 4px 18px rgba(0, 255, 151, 0.25);
  }
  .pg-cta-btn-primary:hover {
    background: #00e084 !important;
    color: #060e1a !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 255, 151, 0.4);
  }
  .pg-cta-btn-demo {
    background: var(--color-primary) !important;
    color: #060e1a !important;
    border: 1px solid var(--color-primary) !important;
    box-shadow: 0 4px 18px rgba(0, 255, 151, 0.25);
  }
  .pg-cta-btn-demo:hover {
    background: #00e084 !important;
    color: #060e1a !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 255, 151, 0.4);
  }
  .pg-cta-btn-plans {
    background: var(--color-primary) !important;
    color: #060e1a !important;
    border: 1px solid var(--color-primary) !important;
    box-shadow: 0 4px 18px rgba(0, 255, 151, 0.25);
  }
  .pg-cta-btn-plans:hover {
    background: #00e084 !important;
    color: #060e1a !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 255, 151, 0.4);
  }

  /* Authentic iBOS Dashboard Window Mockup */
  .pg-preview-window {
    background: #0b1120;
    border: 1px solid rgba(0, 255, 151, 0.3);
    border-radius: 16px;
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.75), 0 0 35px rgba(0, 255, 151, 0.12);
    overflow: hidden;
    margin: 40px auto 0;
    max-width: 1080px;
    backdrop-filter: blur(16px);
  }
  .pg-preview-titlebar {
    background: #070d18;
    padding: 12px 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .pg-preview-dots {
    display: flex;
    gap: 6px;
    align-items: center;
  }
  .pg-preview-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }
  .pg-preview-urlbar {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    padding: 4px 14px;
    font-size: 11px;
    color: #94a3b8;
    font-family: monospace;
  }
  .pg-preview-workspace-badge {
    font-size: 11px;
    color: var(--color-primary);
    background: rgba(0, 255, 151, 0.08);
    border: 1px solid rgba(0, 255, 151, 0.25);
    border-radius: 20px;
    padding: 3px 10px;
    font-weight: 600;
  }

  /* iBOS Header */
  .pg-ibos-header {
    padding: 18px 22px 14px;
    background: #0f172a;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
  }
  .pg-ibos-header-title {
    font-size: 1.15rem;
    font-weight: 700;
    color: #f8fafc;
    margin: 0;
  }
  .pg-ibos-header-sub {
    font-size: 12px;
    color: #94a3b8;
    margin-top: 2px;
  }
  .pg-ibos-header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
  .pg-ibos-prop-select {
    background: #1e293b;
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: #f1f5f9;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
  }
  .pg-ibos-action-btn {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: #f1f5f9;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    cursor: default;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .pg-ibos-action-btn-primary {
    background: var(--color-primary);
    color: #0b1226 !important;
    border: 1px solid var(--color-primary);
    font-weight: 700;
  }

  /* iBOS 8 KPI Stat Grid (PGStats) */
  .pg-ibos-stats-grid {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 8px;
    padding: 14px 22px;
    background: #0b1222;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
  @media (max-width: 1199px) {
    .pg-ibos-stats-grid { grid-template-columns: repeat(4, 1fr); }
  }
  @media (max-width: 575px) {
    .pg-ibos-stats-grid { grid-template-columns: repeat(2, 1fr); }
  }
  .pg-ibos-stat-card {
    background: #131d35;
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 8px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .pg-ibos-stat-label {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .pg-ibos-stat-val {
    font-size: 1.15rem;
    font-weight: 700;
    color: #f8fafc;
    margin-top: 4px;
  }
  .pg-ibos-stat-sub {
    font-size: 10px;
    opacity: 0.75;
    margin-top: 2px;
  }

  /* iBOS Tab Navigation (PGTabs) */
  .pg-ibos-tabs-bar {
    display: flex;
    gap: 4px;
    padding: 10px 22px 0;
    background: #0e162b;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    overflow-x: auto;
  }
  .pg-ibos-tab-btn {
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    color: #94a3b8;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;
    white-space: nowrap;
  }
  .pg-ibos-tab-btn:hover {
    color: #f1f5f9;
  }
  .pg-ibos-tab-btn.active {
    color: var(--color-primary);
    border-bottom-color: var(--color-primary);
    background: rgba(0, 255, 151, 0.04);
  }
  .pg-ibos-tab-badge {
    font-size: 10px;
    padding: 2px 7px;
    border-radius: 10px;
    font-weight: 700;
    background: rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
  }
  .pg-ibos-tab-badge.amber {
    background: rgba(245, 158, 11, 0.2);
    color: #fbbf24;
  }
  .pg-ibos-tab-badge.emerald {
    background: rgba(16, 185, 129, 0.2);
    color: #34d399;
  }

  /* Content Body */
  .pg-ibos-content {
    padding: 20px 22px;
    background: #090e1c;
    min-height: 360px;
  }

  /* Real iBOS PGRoomsBedsTab Floor & Room Cards */
  .pg-floor-divider {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }
  .pg-floor-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
  }
  .pg-floor-line {
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
  }
  .pg-floor-stats {
    font-size: 11px;
    color: #64748b;
  }
  .pg-room-card-h {
    background: #0d162d;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    transition: all 0.2s ease;
    height: 100%;
  }
  .pg-room-card-h:hover {
    border-color: rgba(0, 255, 151, 0.35);
  }
  .pg-room-door-thumb {
    width: 64px;
    min-height: 100%;
    background: #080d1c;
    border-right: 1px solid rgba(255, 255, 255, 0.06);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    flex-shrink: 0;
  }
  .pg-room-body {
    flex: 1;
    padding: 12px 14px;
    min-width: 0;
  }
  .pg-bed-chip-pill {
    font-size: 11px;
    padding: 3px 8px;
    border-radius: 20px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    transition: all 0.15s ease;
  }
  .pg-bed-chip-pill.occupied {
    background: rgba(244, 63, 94, 0.15);
    color: #fda4af;
    border: 1px solid rgba(244, 63, 94, 0.3);
  }
  .pg-bed-chip-pill.vacant {
    background: rgba(16, 185, 129, 0.15);
    color: #6ee7b7;
    border: 1px solid rgba(16, 185, 129, 0.3);
  }
  .pg-bed-chip-pill.notice {
    background: rgba(245, 158, 11, 0.15);
    color: #fcd34d;
    border: 1px solid rgba(245, 158, 11, 0.3);
  }
  .pg-manage-btn {
    font-size: 11px;
    padding: 3px 8px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    color: #cbd5e1;
    cursor: default;
  }
  .pg-manage-btn:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
  }

  /* Tag Badges */
  .pg-ibos-badge-ac {
    background: rgba(56, 189, 248, 0.15);
    color: #38bdf8;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
  }
  .pg-ibos-badge-nonac {
    background: rgba(148, 163, 184, 0.15);
    color: #cbd5e1;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
  }
  .pg-ibos-badge-paid {
    background: rgba(16, 185, 129, 0.15);
    color: #34d399;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
  }
  .pg-ibos-badge-due {
    background: rgba(245, 158, 11, 0.15);
    color: #fbbf24;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
  }
  .pg-ibos-badge-notice {
    background: rgba(249, 115, 22, 0.15);
    color: #fb923c;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
  }
  .pg-ibos-badge-free {
    background: rgba(16, 185, 129, 0.15);
    color: #34d399;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    cursor: default;
  }

  /* Trust Bar */
  .pg-trust-bar {
    background: rgba(15, 23, 48, 0.6);
    border-top: 1px solid rgba(0, 255, 151, 0.15);
    border-bottom: 1px solid rgba(0, 255, 151, 0.15);
    padding: 28px 0;
  }
  .pg-trust-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 8px 12px;
  }
  .pg-trust-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: rgba(0, 255, 151, 0.1);
    color: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
  }
  .pg-trust-title {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 2px;
  }
  .pg-trust-desc {
    font-size: 12px;
    opacity: 0.65;
    margin-bottom: 0;
    line-height: 1.4;
  }

  /* 3-Step Setup */
  .pg-step-card {
    background: rgba(15, 23, 48, 0.65);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    padding: 30px 24px;
    height: 100%;
    position: relative;
    transition: all 0.3s ease;
  }
  .pg-step-card:hover {
    border-color: var(--color-primary);
    transform: translateY(-3px);
  }
  .pg-step-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--color-primary);
    color: #04150d;
    font-weight: 800;
    font-size: 15px;
    margin-bottom: 18px;
  }

  /* 4 Pillars */
  .pg-pillar-card {
    background: rgba(13, 21, 44, 0.75);
    border: 1px solid rgba(0, 255, 151, 0.18);
    border-radius: 18px;
    padding: 34px 30px;
    height: 100%;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
  }
  .pg-pillar-card:hover {
    border-color: var(--color-primary);
    box-shadow: 0 10px 30px rgba(0, 255, 151, 0.1);
  }
  .pg-pillar-badge {
    display: inline-block;
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: var(--color-primary);
    background: rgba(0, 255, 151, 0.1);
    padding: 4px 10px;
    border-radius: 12px;
    margin-bottom: 14px;
  }

  /* Comparison Matrix */
  .pg-compare-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 25px;
    background: rgba(11, 18, 38, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    overflow: hidden;
  }
  .pg-compare-table th,
  .pg-compare-table td {
    padding: 16px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    text-align: left;
    font-size: 14px;
  }
  .pg-compare-table th {
    background: rgba(18, 28, 56, 0.9);
    font-weight: 700;
    color: #fff;
  }
  .pg-compare-table td:nth-child(2) {
    color: var(--color-primary);
    font-weight: 600;
    background: rgba(0, 255, 151, 0.03);
  }

  /* Feature Grid Cards */
  .industry-detail-feature-card {
    background: rgba(15, 20, 40, 0.65);
    border: 1px solid rgba(0,255,151,0.15);
    border-radius: 16px;
    padding: 24px;
    height: 100%;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
  }
  .industry-detail-feature-card:hover {
    border-color: var(--color-primary);
    transform: translateY(-2px);
  }
  .industry-detail-feature-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(0,255,151,0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-primary);
    font-size: 20px;
    margin-bottom: 16px;
  }



  /* Articles */
  .pg-article-link {
    display: block;
    background: rgba(15, 20, 40, 0.6);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px;
    padding: 16px 18px;
    height: 100%;
    color: inherit;
    text-decoration: none;
    font-size: 14px;
    line-height: 1.6;
    transition: all 0.25s ease;
  }
  .pg-article-link:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  .industry-detail-capability-tag {
    display: inline-block;
    background: rgba(0,255,151,0.08);
    border: 1px solid rgba(0,255,151,0.25);
    color: var(--color-primary);
    font-size: 13px;
    padding: 6px 14px;
    border-radius: 20px;
    margin: 4px;
  }
  .industry-detail-module-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }
  @media (max-width: 991px) {
    .industry-detail-module-grid { grid-template-columns: repeat(2, 1fr); }
  }
  .industry-detail-module-item {
    background: rgba(15, 20, 40, 0.65);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px;
    padding: 16px;
    text-align: center;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
  }
  .industry-detail-module-item:hover {
    border-color: var(--color-primary);
  }
  .industry-detail-module-item i {
    font-size: 22px;
    color: var(--color-primary);
    margin-bottom: 8px;
    display: block;
  }
  .industry-detail-module-item span {
    font-size: 13px;
    opacity: 0.85;
  }
  .industry-detail-cta-section {
    background-image: url(${cardBg});
    border-radius: 20px;
  }
`;

const PGPage: React.FC = () => {
  const { t } = useTranslation();

  const title = t("industriesData.pgHostel.title");
  const metaTitle = t("pgPms.metaTitle");
  const metaDescription = t("pgPms.metaDescription");
  const heroTitle = t("pgPms.heroTitle");
  const heroSubtitle = t("pgPms.heroSubtitle");

  const heroBadges = useMemo(() => splitPipe(t("pgPms.heroBadges", { defaultValue: "" })), [t]);
  const whyPoints = useMemo(() => splitPipe(t("pgPms.whyPoints", { defaultValue: "" })), [t]);
  const featureTitles = useMemo(() => splitPipe(t("pgPms.featureTitles", { defaultValue: "" })), [t]);
  const featureDescs = useMemo(() => splitPipe(t("pgPms.featureDescs", { defaultValue: "" })), [t]);
  const meterPoints = useMemo(() => splitPipe(t("pgPms.meterPoints", { defaultValue: "" })), [t]);
  const portalPoints = useMemo(() => splitPipe(t("pgPms.portalPoints", { defaultValue: "" })), [t]);
  const gaps = useMemo(() => splitPipe(t("pgPms.gaps", { defaultValue: "" })), [t]);
  const faqQuestions = useMemo(() => splitPipe(t("pgPms.faqQuestions", { defaultValue: "" })), [t]);
  const faqAnswers = useMemo(() => splitPipe(t("pgPms.faqAnswers", { defaultValue: "" })), [t]);
  const articleTitles = useMemo(() => splitPipe(t("pgPms.articleTitles", { defaultValue: "" })), [t]);

  // Demo status from API
  const [hasDemos, setHasDemos] = useState(false);
  const [previewTab, setPreviewTab] = useState<"rooms" | "billing" | "tenants" | "visitors">("rooms");
  const [activeFaqId, setActiveFaqId] = useState<number | null>(0);

  useEffect(() => {
    getDemosByVertical("pg").then((data) => {
      setHasDemos(data !== null && data.demos.length > 0);
    });
  }, []);

  // Remember vertical intent so signup sets vertical_subtype = pg
  useEffect(() => {
    rememberVerticalIntent("pg");
  }, []);

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "AXOIX PG & Hostel Management",
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "PG & Hostel Management Software",
    operatingSystem: "Web",
    description: metaDescription,
    url: `https://axoix.com${PAGE_PATH}`,
    provider: {
      "@type": "Organization",
      name: "AXOIX TECHNOLOGIES PRIVATE LIMITED",
      url: "https://axoix.com",
    },
    areaServed: { "@type": "Country", name: "India" },
    featureList: featureTitles,
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "INR",
      lowPrice: "0",
      highPrice: "14999",
      offerCount: "4",
    },
  };

  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqQuestions.map((question, idx) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faqAnswers[idx] || "",
      },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://axoix.com/" },
      { "@type": "ListItem", position: 2, name: "Industries", item: "https://axoix.com/industries" },
      { "@type": "ListItem", position: 3, name: title, item: `https://axoix.com${PAGE_PATH}` },
    ],
  };

  return (
    <Fragment>
      <SEO
        title={metaTitle}
        description={metaDescription}
        path={PAGE_PATH}
        schema={[softwareSchema, faqPageSchema, breadcrumbSchema]}
      />
      <style>{pageStyles}</style>
      <div className="about-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main
            style={{
              backgroundImage: `url(${serviceBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center top",
              backgroundRepeat: "no-repeat",
              backgroundAttachment: "fixed",
            }}
          >
            {/* =========================================================================
                HERO SECTION — Outcome-Focused, Risk-Free Guarantee & UI Preview
               ========================================================================= */}
            <section className="pg-hero-section">
              <div className="container text-center">
                {/* Free plan hook pill */}
                <div className="pg-badge-pill">
                  <i className="fas fa-sparkles"></i>
                  <span>{t("pgPms.heroTrustBadge", { defaultValue: "100% Free Forever up to 20 Beds • No Credit Card Required" })}</span>
                </div>

                <h1 className="pg-hero-title">
                  {heroTitle}
                </h1>

                <p className="pg-hero-desc">
                  {heroSubtitle}
                </p>

                {/* Primary CTA button group — uniform pill styling matching theme */}
                <div className="pg-hero-btn-group justify-content-center mb-3">
                  <Link
                    to="/signup?vertical=pg&from=pg"
                    className="pg-cta-btn pg-cta-btn-primary"
                  >
                    {t("pgPms.ctaPrimary", { defaultValue: "Start Free with 20 Beds" })}
                  </Link>

                  {hasDemos && (
                    <Link
                      to="/demo/pg"
                      className="pg-cta-btn pg-cta-btn-demo"
                    >
                      <i className="fas fa-play-circle me-1"></i>
                      {t("pgPms.ctaDemo", { defaultValue: "Explore Live PG Demo" })}
                    </Link>
                  )}

                  <a
                    href="#pg-pricing"
                    className="pg-cta-btn pg-cta-btn-plans"
                  >
                    {t("pgPms.ctaPricing", { defaultValue: "View All Plans" })}
                  </a>
                </div>

                <div className="pg-hero-trust-bullets">
                  <span><i className="fas fa-check-circle" style={{ color: "var(--color-primary)" }}></i> 2-Minute Bulk Floor Setup</span>
                  <span><i className="fas fa-check-circle" style={{ color: "var(--color-primary)" }}></i> Instant UPI QR & Receipts</span>
                  <span><i className="fas fa-check-circle" style={{ color: "var(--color-primary)" }}></i> No Credit Card Required</span>
                  <Link to="/compare/rentok" className="opacity-75 ms-2" style={{ color: "var(--color-primary)", textDecoration: "underline" }}>
                    {t("pgPms.compareLink", { defaultValue: "Compare vs RentOk" })} →
                  </Link>
                </div>

                {/* -------------------------------------------------------------
                    Interactive Real iBOS PG Dashboard Mockup
                   ------------------------------------------------------------- */}
                <div className="pg-preview-window text-start">
                  {/* Browser Window Titlebar */}
                  <div className="pg-preview-titlebar">
                    <div className="pg-preview-dots">
                      <span className="pg-preview-dot" style={{ background: "#ff5f56" }}></span>
                      <span className="pg-preview-dot" style={{ background: "#ffbd2e" }}></span>
                      <span className="pg-preview-dot" style={{ background: "#27c93f" }}></span>
                    </div>
                    <div className="pg-preview-urlbar d-none d-md-inline-flex">
                      <i className="fas fa-lock" style={{ color: "var(--color-primary)", fontSize: "10px" }}></i>
                      <span>https://app.axoix.io/hotel/pg-management</span>
                    </div>
                    <span className="pg-preview-workspace-badge">
                      <i className="fas fa-circle-check me-1"></i> iBOS PG Core
                    </span>
                  </div>

                  {/* Real iBOS PG Header */}
                  <div className="pg-ibos-header">
                    <div>
                      <h3 className="pg-ibos-header-title">PG / Hostel Management</h3>
                      <div className="pg-ibos-header-sub">
                        Bed allocation, tenant tracking, monthly billing, visitor log
                      </div>
                    </div>
                    <div className="pg-ibos-header-actions">
                      <div className="pg-ibos-prop-select">
                        <i className="fas fa-building me-1 opacity-75"></i> Green Leaf PG — Koramangala (Branch 1) ▾
                      </div>
                      <span className="pg-ibos-action-btn">
                        <i className="fas fa-plus"></i> Add Room
                      </span>
                      <span className="pg-ibos-action-btn">
                        <i className="fas fa-bolt"></i> Bulk Setup
                      </span>
                      <span className="pg-ibos-action-btn pg-ibos-action-btn-primary">
                        <i className="fas fa-user-plus"></i> + New Tenant
                      </span>
                    </div>
                  </div>

                  {/* Real iBOS 8 KPI Tiles (PGStats) */}
                  <div className="pg-ibos-stats-grid">
                    <div className="pg-ibos-stat-card">
                      <div className="pg-ibos-stat-label">
                        <span>Total Rooms</span>
                        <i className="fas fa-door-open" style={{ color: "#818cf8" }}></i>
                      </div>
                      <div className="pg-ibos-stat-val">16</div>
                      <div className="pg-ibos-stat-sub text-muted">Across 3 Floors</div>
                    </div>
                    <div className="pg-ibos-stat-card">
                      <div className="pg-ibos-stat-label">
                        <span>Total Beds</span>
                        <i className="fas fa-bed" style={{ color: "#94a3b8" }}></i>
                      </div>
                      <div className="pg-ibos-stat-val">48</div>
                      <div className="pg-ibos-stat-sub text-muted">All configurations</div>
                    </div>
                    <div className="pg-ibos-stat-card">
                      <div className="pg-ibos-stat-label">
                        <span>Occupied</span>
                        <i className="fas fa-user-check" style={{ color: "#f43f5e" }}></i>
                      </div>
                      <div className="pg-ibos-stat-val" style={{ color: "#fb7185" }}>44</div>
                      <div className="pg-ibos-stat-sub" style={{ color: "#fb7185" }}>Active stays</div>
                    </div>
                    <div className="pg-ibos-stat-card">
                      <div className="pg-ibos-stat-label">
                        <span>Available</span>
                        <i className="fas fa-key" style={{ color: "#34d399" }}></i>
                      </div>
                      <div className="pg-ibos-stat-val" style={{ color: "#34d399" }}>4</div>
                      <div className="pg-ibos-stat-sub" style={{ color: "#34d399" }}>Ready to move</div>
                    </div>
                    <div className="pg-ibos-stat-card">
                      <div className="pg-ibos-stat-label">
                        <span>Occupancy</span>
                        <i className="fas fa-chart-pie" style={{ color: "#c084fc" }}></i>
                      </div>
                      <div className="pg-ibos-stat-val" style={{ color: "#c084fc" }}>91.7%</div>
                      <div className="pg-ibos-stat-sub" style={{ color: "#c084fc" }}>Healthy ratio</div>
                    </div>
                    <div className="pg-ibos-stat-card">
                      <div className="pg-ibos-stat-label">
                        <span>Active Tenants</span>
                        <i className="fas fa-users" style={{ color: "#60a5fa" }}></i>
                      </div>
                      <div className="pg-ibos-stat-val" style={{ color: "#60a5fa" }}>44</div>
                      <div className="pg-ibos-stat-sub" style={{ color: "#60a5fa" }}>KYC on file</div>
                    </div>
                    <div className="pg-ibos-stat-card">
                      <div className="pg-ibos-stat-label">
                        <span>Pending Bills</span>
                        <i className="fas fa-receipt" style={{ color: "#fbbf24" }}></i>
                      </div>
                      <div className="pg-ibos-stat-val" style={{ color: "#fbbf24" }}>3</div>
                      <div className="pg-ibos-stat-sub" style={{ color: "#fbbf24" }}>₹28,500 due</div>
                    </div>
                    <div className="pg-ibos-stat-card">
                      <div className="pg-ibos-stat-label">
                        <span>Today Visitors</span>
                        <i className="fas fa-id-badge" style={{ color: "#22d3ee" }}></i>
                      </div>
                      <div className="pg-ibos-stat-val" style={{ color: "#22d3ee" }}>5</div>
                      <div className="pg-ibos-stat-sub" style={{ color: "#22d3ee" }}>Gate verified</div>
                    </div>
                  </div>

                  {/* Real iBOS Tab Navigation (PGTabs) */}
                  <div className="pg-ibos-tabs-bar">
                    <button
                      className={`pg-ibos-tab-btn ${previewTab === "rooms" ? "active" : ""}`}
                      onClick={() => setPreviewTab("rooms")}
                    >
                      <i className="fas fa-bed"></i> Rooms & Beds
                      <span className="pg-ibos-tab-badge">48</span>
                    </button>
                    <button
                      className={`pg-ibos-tab-btn ${previewTab === "billing" ? "active" : ""}`}
                      onClick={() => setPreviewTab("billing")}
                    >
                      <i className="fas fa-file-invoice-dollar"></i> Monthly Billing
                      <span className="pg-ibos-tab-badge amber">3 Due</span>
                    </button>
                    <button
                      className={`pg-ibos-tab-btn ${previewTab === "tenants" ? "active" : ""}`}
                      onClick={() => setPreviewTab("tenants")}
                    >
                      <i className="fas fa-users"></i> Tenants
                      <span className="pg-ibos-tab-badge emerald">44</span>
                    </button>
                    <button
                      className={`pg-ibos-tab-btn ${previewTab === "visitors" ? "active" : ""}`}
                      onClick={() => setPreviewTab("visitors")}
                    >
                      <i className="fas fa-user-clock"></i> Visitor Log
                      <span className="pg-ibos-tab-badge">5 Today</span>
                    </button>
                  </div>

                  {/* Interactive Content Body */}
                  <div className="pg-ibos-content">
                    {previewTab === "rooms" && (
                      <div>
                        {/* Floor Controls & Legend (matches PGRoomsBedsTab.jsx) */}
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                          <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: "12px" }}>
                            <span className="d-inline-flex align-items-center gap-1.5">
                              <span style={{ width: 10, height: 10, borderRadius: 3, background: "#10b981", display: "inline-block" }}></span> Available (4)
                            </span>
                            <span className="d-inline-flex align-items-center gap-1.5 ms-2">
                              <span style={{ width: 10, height: 10, borderRadius: 3, background: "#f43f5e", display: "inline-block" }}></span> Occupied (44)
                            </span>
                            <span className="d-inline-flex align-items-center gap-1.5 ms-2">
                              <span style={{ width: 10, height: 10, borderRadius: 3, background: "#f59e0b", display: "inline-block" }}></span> Maintenance (1)
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span className="pg-manage-btn" style={{ background: "#7c3aed", borderColor: "#7c3aed", color: "#fff", fontWeight: 600 }}>
                              + Add Room
                            </span>
                            <span className="pg-manage-btn">
                              Bulk Setup
                            </span>
                          </div>
                        </div>

                        {/* FLOOR 1 */}
                        <div className="pg-floor-divider">
                          <span className="pg-floor-label">Floor 1</span>
                          <div className="pg-floor-line"></div>
                          <span className="pg-floor-stats">3 room(s) · 6/7 beds occupied</span>
                        </div>
                        <div className="row g-3 mb-4">
                          {/* Room 101 */}
                          <div className="col-lg-4 col-md-6">
                            <div className="pg-room-card-h">
                              <div className="pg-room-door-thumb">🚪</div>
                              <div className="pg-room-body">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <div className="d-flex align-items-center gap-1">
                                    <strong className="text-white" style={{ fontSize: "13px" }}>Room 101</strong>
                                    <span className="pg-ibos-badge-ac">2-Sharing AC</span>
                                  </div>
                                  <span className="pg-manage-btn">Manage</span>
                                </div>
                                <div className="text-white-50 mb-2" style={{ fontSize: "11px" }}>2 bed(s) · ₹8,500/bed</div>
                                <div className="d-flex flex-wrap gap-1 mb-2">
                                  <span className="pg-bed-chip-pill occupied">101-A · Rahul</span>
                                  <span className="pg-bed-chip-pill vacant">101-B · Free</span>
                                </div>
                                <div style={{ fontSize: "10.5px", color: "#38bdf8" }}>
                                  <i className="fas fa-bolt me-1"></i> Sub-Meter: 482 kWh
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Room 102 */}
                          <div className="col-lg-4 col-md-6">
                            <div className="pg-room-card-h">
                              <div className="pg-room-door-thumb">🚪</div>
                              <div className="pg-room-body">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <div className="d-flex align-items-center gap-1">
                                    <strong className="text-white" style={{ fontSize: "13px" }}>Room 102</strong>
                                    <span className="pg-ibos-badge-nonac">3-Sharing Non-AC</span>
                                  </div>
                                  <span className="pg-manage-btn">Manage</span>
                                </div>
                                <div className="text-white-50 mb-2" style={{ fontSize: "11px" }}>3 bed(s) · ₹6,500/bed</div>
                                <div className="d-flex flex-wrap gap-1 mb-2">
                                  <span className="pg-bed-chip-pill occupied">102-A · Amit</span>
                                  <span className="pg-bed-chip-pill occupied">102-B · Vikram</span>
                                  <span className="pg-bed-chip-pill notice">102-C · Pooja</span>
                                </div>
                                <div style={{ fontSize: "10.5px", color: "#38bdf8" }}>
                                  <i className="fas fa-bolt me-1"></i> Sub-Meter: 194 kWh
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Room 103 */}
                          <div className="col-lg-4 col-md-6">
                            <div className="pg-room-card-h">
                              <div className="pg-room-door-thumb">🚪</div>
                              <div className="pg-room-body">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <div className="d-flex align-items-center gap-1">
                                    <strong className="text-white" style={{ fontSize: "13px" }}>Room 103</strong>
                                    <span className="pg-ibos-badge-ac">2-Sharing AC</span>
                                  </div>
                                  <span className="pg-manage-btn">Manage</span>
                                </div>
                                <div className="text-white-50 mb-2" style={{ fontSize: "11px" }}>2 bed(s) · ₹9,000/bed</div>
                                <div className="d-flex flex-wrap gap-1 mb-2">
                                  <span className="pg-bed-chip-pill occupied">103-A · Sneha</span>
                                  <span className="pg-bed-chip-pill occupied">103-B · Neha</span>
                                </div>
                                <div style={{ fontSize: "10.5px", color: "#38bdf8" }}>
                                  <i className="fas fa-bolt me-1"></i> Sub-Meter: 310 kWh
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* FLOOR 2 */}
                        <div className="pg-floor-divider">
                          <span className="pg-floor-label">Floor 2</span>
                          <div className="pg-floor-line"></div>
                          <span className="pg-floor-stats">2 room(s) · 3/4 beds occupied</span>
                        </div>
                        <div className="row g-3">
                          {/* Room 201 */}
                          <div className="col-lg-4 col-md-6">
                            <div className="pg-room-card-h">
                              <div className="pg-room-door-thumb">🚪</div>
                              <div className="pg-room-body">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <div className="d-flex align-items-center gap-1">
                                    <strong className="text-white" style={{ fontSize: "13px" }}>Room 201</strong>
                                    <span className="pg-ibos-badge-ac">2-Sharing AC</span>
                                  </div>
                                  <span className="pg-manage-btn">Manage</span>
                                </div>
                                <div className="text-white-50 mb-2" style={{ fontSize: "11px" }}>2 bed(s) · ₹8,500/bed</div>
                                <div className="d-flex flex-wrap gap-1 mb-2">
                                  <span className="pg-bed-chip-pill occupied">201-A · Ankit</span>
                                  <span className="pg-bed-chip-pill vacant">201-B · Free</span>
                                </div>
                                <div style={{ fontSize: "10.5px", color: "#38bdf8" }}>
                                  <i className="fas fa-bolt me-1"></i> Sub-Meter: 140 kWh
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Room 202 */}
                          <div className="col-lg-4 col-md-6">
                            <div className="pg-room-card-h">
                              <div className="pg-room-door-thumb">🚪</div>
                              <div className="pg-room-body">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <div className="d-flex align-items-center gap-1">
                                    <strong className="text-white" style={{ fontSize: "13px" }}>Room 202</strong>
                                    <span className="pg-ibos-badge-nonac">2-Sharing Non-AC</span>
                                  </div>
                                  <span className="pg-manage-btn">Manage</span>
                                </div>
                                <div className="text-white-50 mb-2" style={{ fontSize: "11px" }}>2 bed(s) · ₹6,500/bed</div>
                                <div className="d-flex flex-wrap gap-1 mb-2">
                                  <span className="pg-bed-chip-pill occupied">202-A · Priya</span>
                                  <span className="pg-bed-chip-pill occupied">202-B · Dev</span>
                                </div>
                                <div style={{ fontSize: "10.5px", color: "#38bdf8" }}>
                                  <i className="fas fa-bolt me-1"></i> Sub-Meter: 182 kWh
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {previewTab === "billing" && (
                      <div>
                        {/* Real iBOS Billing Summary Cards (PGBillSummary.jsx) */}
                        <div className="row g-2 mb-3">
                          <div className="col-3">
                            <div className="p-2 rounded" style={{ background: "#0e172e", border: "1px solid rgba(255,255,255,0.08)" }}>
                              <div className="fw-bold text-white" style={{ fontSize: "16px" }}>44</div>
                              <div className="text-white-50" style={{ fontSize: "11px" }}>Total Bills</div>
                            </div>
                          </div>
                          <div className="col-3">
                            <div className="p-2 rounded" style={{ background: "#0e172e", border: "1px solid rgba(255,255,255,0.08)" }}>
                              <div className="fw-bold" style={{ color: "#34d399", fontSize: "16px" }}>40</div>
                              <div className="text-white-50" style={{ fontSize: "11px" }}>Paid</div>
                            </div>
                          </div>
                          <div className="col-3">
                            <div className="p-2 rounded" style={{ background: "#0e172e", border: "1px solid rgba(255,255,255,0.08)" }}>
                              <div className="fw-bold" style={{ color: "#f43f5e", fontSize: "16px" }}>4</div>
                              <div className="text-white-50" style={{ fontSize: "11px" }}>Unpaid</div>
                            </div>
                          </div>
                          <div className="col-3">
                            <div className="p-2 rounded" style={{ background: "#0e172e", border: "1px solid rgba(255,255,255,0.08)" }}>
                              <div className="fw-bold" style={{ color: "#fbbf24", fontSize: "16px" }}>₹28,500</div>
                              <div className="text-white-50" style={{ fontSize: "11px" }}>Outstanding</div>
                            </div>
                          </div>
                        </div>

                        {/* Billing Controls */}
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                          <div className="d-flex align-items-center gap-2" style={{ fontSize: "11.5px" }}>
                            <span className="badge bg-primary-subtle text-primary border border-primary">Cycle: Oct 2026</span>
                            <span className="text-white-50">Auto-prorated by tenant-days</span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span className="pg-ibos-action-btn" style={{ fontSize: "11px", padding: "4px 10px" }}>
                              <i className="fas fa-bolt text-warning"></i> Auto-Split Sub-Meters
                            </span>
                            <span className="pg-ibos-action-btn" style={{ fontSize: "11px", padding: "4px 10px" }}>
                              <i className="fab fa-whatsapp text-success"></i> WhatsApp Reminders
                            </span>
                          </div>
                        </div>

                        {/* Real iBOS Billing Table (matches PGBillTable.jsx & PGBillRow.jsx) */}
                        <div className="table-responsive">
                          <table className="table table-dark table-sm mb-0" style={{ fontSize: "12px", background: "transparent" }}>
                            <thead>
                              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.15)", opacity: 0.8 }}>
                                <th>Invoice & Bill #</th>
                                <th>Type</th>
                                <th>Resident</th>
                                <th>Period</th>
                                <th>Base Rent</th>
                                <th>Utilities & Topups</th>
                                <th>Total</th>
                                <th>Status</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                                <td>
                                  <div style={{ color: "#38bdf8", fontFamily: "monospace", fontSize: "11px" }}>GST-2026-0891</div>
                                  <div className="text-white-50" style={{ fontSize: "10px" }}>PG-BL-0101</div>
                                </td>
                                <td><span className="badge" style={{ background: "rgba(100,116,139,0.3)", color: "#cbd5e1", fontSize: "10px" }}>🏠⚡ Combined</span></td>
                                <td><strong>Rahul Sharma</strong><div className="text-white-50" style={{ fontSize: "10px" }}>101-A (2-AC)</div></td>
                                <td style={{ fontSize: "11px" }}>01 Oct → 31 Oct</td>
                                <td>₹8,500</td>
                                <td style={{ fontSize: "11px" }}>Elec: ₹420 (42u)</td>
                                <td className="fw-bold">₹8,920</td>
                                <td><span className="pg-ibos-badge-paid">UPI Auto-Matched ✓</span></td>
                                <td><span className="text-muted" style={{ cursor: "default" }}>Receipt ↗</span></td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                                <td>
                                  <div style={{ color: "#38bdf8", fontFamily: "monospace", fontSize: "11px" }}>GST-2026-0892</div>
                                  <div className="text-white-50" style={{ fontSize: "10px" }}>PG-BL-0102</div>
                                </td>
                                <td><span className="badge" style={{ background: "rgba(100,116,139,0.3)", color: "#cbd5e1", fontSize: "10px" }}>🏠⚡ Combined</span></td>
                                <td><strong>Vikram Patel</strong><div className="text-white-50" style={{ fontSize: "10px" }}>102-B (3-Non-AC)</div></td>
                                <td style={{ fontSize: "11px" }}>01 Oct → 31 Oct</td>
                                <td>₹6,500</td>
                                <td style={{ fontSize: "11px" }}>Elec: ₹280 (28u)</td>
                                <td className="fw-bold">₹6,780</td>
                                <td><span className="pg-ibos-badge-due">Unpaid (Due 5th)</span></td>
                                <td><span style={{ color: "var(--color-primary)", cursor: "default" }}>Send UPI QR</span></td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                                <td>
                                  <div style={{ color: "#fbbf24", fontFamily: "monospace", fontSize: "11px" }}>pending</div>
                                  <div className="text-white-50" style={{ fontSize: "10px" }}>PG-BL-0103</div>
                                </td>
                                <td><span className="badge" style={{ background: "rgba(245,158,11,0.2)", color: "#fcd34d", fontSize: "10px" }}>📌 One-off</span></td>
                                <td><strong>Pooja Roy</strong><div className="text-white-50" style={{ fontSize: "10px" }}>102-C (Vacating)</div></td>
                                <td style={{ fontSize: "11px" }}>Move-out Settlement</td>
                                <td>—</td>
                                <td style={{ fontSize: "11px" }}>Deductions: ₹1,500</td>
                                <td className="fw-bold text-success">Net: ₹11,500</td>
                                <td><span className="pg-ibos-badge-notice">Notice Escrow</span></td>
                                <td><span style={{ color: "#38bdf8", cursor: "default" }}>Settle ↗</span></td>
                              </tr>
                              <tr>
                                <td>
                                  <div style={{ color: "#38bdf8", fontFamily: "monospace", fontSize: "11px" }}>GST-2026-0894</div>
                                  <div className="text-white-50" style={{ fontSize: "10px" }}>PG-BL-0104</div>
                                </td>
                                <td><span className="badge" style={{ background: "rgba(56,189,248,0.2)", color: "#38bdf8", fontSize: "10px" }}>⚡ Utility</span></td>
                                <td><strong>Sneha Rao</strong><div className="text-white-50" style={{ fontSize: "10px" }}>103-A (2-AC)</div></td>
                                <td style={{ fontSize: "11px" }}>01 Oct → 31 Oct</td>
                                <td>—</td>
                                <td style={{ fontSize: "11px" }}>Elec: ₹520 (52u)</td>
                                <td className="fw-bold">₹520</td>
                                <td><span className="pg-ibos-badge-paid">UPI Auto-Matched ✓</span></td>
                                <td><span className="text-muted" style={{ cursor: "default" }}>Receipt ↗</span></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {previewTab === "tenants" && (
                      <div>
                        {/* Tenant Registry Controls */}
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                          <div style={{ fontSize: "12px", opacity: 0.8 }}>
                            <i className="fas fa-users me-1 text-primary"></i> 44 Active Residents · 1 Notice Period
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span className="pg-ibos-action-btn" style={{ fontSize: "11px", padding: "4px 10px" }}>
                              <i className="fas fa-file-export"></i> Export Police KYC
                            </span>
                            <span className="pg-ibos-action-btn pg-ibos-action-btn-primary" style={{ fontSize: "11px", padding: "4px 10px" }}>
                              <i className="fas fa-user-plus"></i> Add Resident
                            </span>
                          </div>
                        </div>

                        {/* Tenant Registry Table */}
                        <div className="table-responsive">
                          <table className="table table-dark table-sm mb-0" style={{ fontSize: "12px", background: "transparent" }}>
                            <thead>
                              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.15)", opacity: 0.8 }}>
                                <th>Resident Name</th>
                                <th>Bed Allotment</th>
                                <th>Phone / WhatsApp</th>
                                <th>Move-in Date</th>
                                <th>Deposit (Escrow)</th>
                                <th>KYC Verification</th>
                                <th>Monthly Rent</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                                <td><strong>Rahul Sharma</strong></td>
                                <td><span className="pg-ibos-badge-ac">101-A</span></td>
                                <td>+91 98765 43210</td>
                                <td>12 Aug 2025</td>
                                <td>₹17,000 (Locked)</td>
                                <td><span className="pg-ibos-badge-paid"><i className="fas fa-shield-alt me-1"></i> Aadhaar Verified</span></td>
                                <td className="fw-bold">₹8,500/mo</td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                                <td><strong>Vikram Patel</strong></td>
                                <td><span className="pg-ibos-badge-nonac">102-B</span></td>
                                <td>+91 98112 23344</td>
                                <td>01 Jan 2026</td>
                                <td>₹13,000 (Locked)</td>
                                <td><span className="pg-ibos-badge-paid"><i className="fas fa-shield-alt me-1"></i> Aadhaar Verified</span></td>
                                <td className="fw-bold">₹6,500/mo</td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                                <td><strong>Pooja Roy</strong></td>
                                <td><span className="pg-ibos-badge-nonac">102-C</span></td>
                                <td>+91 99001 55667</td>
                                <td>15 Feb 2026</td>
                                <td>₹13,000 (Locked)</td>
                                <td><span className="pg-ibos-badge-notice"><i className="fas fa-clock me-1"></i> Vacating 18th</span></td>
                                <td className="fw-bold">₹6,500/mo</td>
                              </tr>
                              <tr>
                                <td><strong>Sneha Rao</strong></td>
                                <td><span className="pg-ibos-badge-ac">103-A</span></td>
                                <td>+91 98450 11223</td>
                                <td>01 Sep 2025</td>
                                <td>₹18,000 (Locked)</td>
                                <td><span className="pg-ibos-badge-paid"><i className="fas fa-shield-alt me-1"></i> Aadhaar Verified</span></td>
                                <td className="fw-bold">₹9,000/mo</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {previewTab === "visitors" && (
                      <div>
                        {/* Visitor Log Controls */}
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                          <div style={{ fontSize: "12px", opacity: 0.8 }}>
                            <i className="fas fa-id-badge me-1 text-info"></i> Today's Visitor Gate Log · 2 On-Premise · 3 Checked Out
                          </div>
                          <span className="pg-ibos-action-btn" style={{ fontSize: "11px", padding: "4px 10px" }}>
                            <i className="fas fa-qrcode"></i> Quick Gate OTP Check-in
                          </span>
                        </div>

                        {/* Visitor Log Table */}
                        <div className="table-responsive">
                          <table className="table table-dark table-sm mb-0" style={{ fontSize: "12px", background: "transparent" }}>
                            <thead>
                              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.15)", opacity: 0.8 }}>
                                <th>Visitor Name</th>
                                <th>Visiting Resident</th>
                                <th>Purpose</th>
                                <th>Gate OTP Status</th>
                                <th>In Time</th>
                                <th>Out Time</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                                <td><strong>Ramesh Sharma</strong> (Father)</td>
                                <td>Rahul S. (101-A)</td>
                                <td>Family Visit</td>
                                <td><span className="pg-ibos-badge-paid">Verified (4921)</span></td>
                                <td>10:15 AM</td>
                                <td>01:30 PM</td>
                                <td><span className="opacity-75">Checked Out</span></td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                                <td><strong>Arjun Verma</strong> (Friend)</td>
                                <td>Vikram P. (102-B)</td>
                                <td>Study / Project</td>
                                <td><span className="pg-ibos-badge-paid">Verified (8310)</span></td>
                                <td>02:45 PM</td>
                                <td>—</td>
                                <td><span className="pg-ibos-badge-due">On Campus</span></td>
                              </tr>
                              <tr>
                                <td><strong>Sunita Roy</strong> (Mother)</td>
                                <td>Pooja R. (102-C)</td>
                                <td>Packing Assistance</td>
                                <td><span className="pg-ibos-badge-paid">Verified (1209)</span></td>
                                <td>04:00 PM</td>
                                <td>—</td>
                                <td><span className="pg-ibos-badge-due">On Campus</span></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </section>

            {/* =========================================================================
                OPERATOR TRUST BAR — Key Operational Advantages
               ========================================================================= */}
            <section className="pg-trust-bar">
              <div className="container">
                <div className="row g-3">
                  <div className="col-lg-3 col-md-6">
                    <div className="pg-trust-item">
                      <div className="pg-trust-icon"><i className="fas fa-bed"></i></div>
                      <div>
                        <div className="pg-trust-title">Indian PG Architecture</div>
                        <p className="pg-trust-desc">Beds not rooms, monthly proration, roommate sharing types.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <div className="pg-trust-item">
                      <div className="pg-trust-icon"><i className="fas fa-bolt"></i></div>
                      <div>
                        <div className="pg-trust-title">Sub-Metered by Days</div>
                        <p className="pg-trust-desc">Fair electricity split by tenant-days, ending roommate disputes.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <div className="pg-trust-item">
                      <div className="pg-trust-icon"><i className="fas fa-qrcode"></i></div>
                      <div>
                        <div className="pg-trust-title">UPI QR & Bank Match</div>
                        <p className="pg-trust-desc">Razorpay, UPI QR invoices, and automated bank passbook matching.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <div className="pg-trust-item">
                      <div className="pg-trust-icon"><i className="fas fa-gift"></i></div>
                      <div>
                        <div className="pg-trust-title">Free Forever (20 Beds)</div>
                        <p className="pg-trust-desc">Zero platform fees for small properties. Upgrade only as you grow.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* =========================================================================
                3-STEP FAST SETUP — Live in 3 Minutes
               ========================================================================= */}
            <section className="pt-80 pb-60">
              <div className="container">
                <div className="text-center mb-50">
                  <span className="sub-title mb-15">Zero Technical Setup Required</span>
                  <h2 className="title">Set Up Your Whole PG in 3 Simple Steps</h2>
                  <p className="opacity-75 mt-15" style={{ maxWidth: 680, margin: "15px auto 0" }}>
                    No complex software configuration. Enter your property details and start running your rent cycle today.
                  </p>
                </div>

                <div className="row g-4">
                  <div className="col-lg-4 col-md-6">
                    <div className="pg-step-card">
                      <span className="pg-step-num">1</span>
                      <h3 style={{ fontSize: "1.2rem", marginBottom: "12px", color: "#fff" }}>
                        Bulk Generate Floors & Beds
                      </h3>
                      <p className="opacity-75 mb-0" style={{ fontSize: "14px", lineHeight: 1.7 }}>
                        Use our 60-second bulk wizard: define your floors, room numbers, and sharing types (Single, Double, Triple, 4-Sharing). Every bed is generated automatically with its own monthly rate.
                      </p>
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6">
                    <div className="pg-step-card">
                      <span className="pg-step-num">2</span>
                      <h3 style={{ fontSize: "1.2rem", marginBottom: "12px", color: "#fff" }}>
                        Onboard Residents & Digital KYC
                      </h3>
                      <p className="opacity-75 mb-0" style={{ fontSize: "14px", lineHeight: 1.7 }}>
                        Add existing residents directly or send a self-service digital link. Capture Aadhaar/ID proof, guardian contacts, security deposit amount, and joining dates with encrypted privacy storage.
                      </p>
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6">
                    <div className="pg-step-card">
                      <span className="pg-step-num">3</span>
                      <h3 style={{ fontSize: "1.2rem", marginBottom: "12px", color: "#fff" }}>
                        1-Click Rent Runs & Collections
                      </h3>
                      <p className="opacity-75 mb-0" style={{ fontSize: "14px", lineHeight: 1.7 }}>
                        Log room electricity meters and run monthly rent in 1 click. Automated invoices with UPI QR codes deliver instantly via email and resident portal, with official receipts issued upon payment.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* =========================================================================
                THE 4 CORE OPERATIONAL PILLARS — Benefit Deep Dives
               ========================================================================= */}
            <section className="pt-40 pb-60">
              <div className="container">
                <div className="text-center mb-50">
                  <span className="sub-title mb-15">Operational Excellence</span>
                  <h2 className="title">The 4 Pillars That Stop Daily Landlord Headaches</h2>
                </div>

                <div className="row g-4">
                  {/* Pillar 1 */}
                  <div className="col-lg-6">
                    <div className="pg-pillar-card">
                      <span className="pg-pillar-badge">Pillar 1: Inventory</span>
                      <h3 style={{ fontSize: "1.35rem", marginBottom: "14px", color: "#fff" }}>
                        <i className="fas fa-bed me-2" style={{ color: "var(--color-primary)" }}></i>
                        Interactive Bed Allocation Matrix
                      </h3>
                      <p className="opacity-80 mb-20" style={{ lineHeight: 1.7 }}>
                        Know exactly which bed is empty across every floor. Handle mid-month room shifts, student vacates, and sharing upgrades without breaking your accounting books.
                      </p>
                      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: "14px" }}>
                        <li className="mb-2"><i className="fas fa-check text-primary me-2" style={{ color: "var(--color-primary)" }}></i> Single, 2-sharing, 3-sharing & dorm bed tracking with distinct monthly rates.</li>
                        <li className="mb-2"><i className="fas fa-check text-primary me-2" style={{ color: "var(--color-primary)" }}></i> Auto-calculated prorated rent for mid-month move-ins and move-outs.</li>
                        <li className="mb-2"><i className="fas fa-check text-primary me-2" style={{ color: "var(--color-primary)" }}></i> 1-click room & bed transfer with automatic deposit balance carry-forward.</li>
                      </ul>
                    </div>
                  </div>

                  {/* Pillar 2 */}
                  <div className="col-lg-6">
                    <div className="pg-pillar-card">
                      <span className="pg-pillar-badge">Pillar 2: Collections</span>
                      <h3 style={{ fontSize: "1.35rem", marginBottom: "14px", color: "#fff" }}>
                        <i className="fas fa-file-invoice-rupee-sign me-2" style={{ color: "var(--color-primary)" }}></i>
                        1-Click Rent Runs & Bank Matching
                      </h3>
                      <p className="opacity-80 mb-20" style={{ lineHeight: 1.7 }}>
                        Stop spending the first week of every month chasing tenants. Run automated property-wide rent generation with itemized utility charges, late fee rules, and instant UPI payment links.
                      </p>
                      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: "14px" }}>
                        <li className="mb-2"><i className="fas fa-check text-primary me-2" style={{ color: "var(--color-primary)" }}></i> Unified monthly bills combining Rent, Electricity, Meals, and Water.</li>
                        <li className="mb-2"><i className="fas fa-check text-primary me-2" style={{ color: "var(--color-primary)" }}></i> Automated bank statement matching to verify direct UPI and IMPS transfers.</li>
                        <li className="mb-2"><i className="fas fa-check text-primary me-2" style={{ color: "var(--color-primary)" }}></i> Digital rent receipts issued automatically for tenant HRA tax exemptions.</li>
                      </ul>
                    </div>
                  </div>

                  {/* Pillar 3 */}
                  <div className="col-lg-6">
                    <div className="pg-pillar-card">
                      <span className="pg-pillar-badge">Pillar 3: Utilities</span>
                      <h3 style={{ fontSize: "1.35rem", marginBottom: "14px", color: "#fff" }}>
                        <i className="fas fa-bolt me-2" style={{ color: "var(--color-primary)" }}></i>
                        Dispute-Free Electricity Sub-Metering
                      </h3>
                      <p className="opacity-80 mb-20" style={{ lineHeight: 1.7 }}>
                        The #1 source of tenant fights in Indian PGs is splitting the AC bill. AXOIX uses a proven day-weighted formula that divides consumed units strictly by the days each roommate occupied the room.
                      </p>
                      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: "14px" }}>
                        <li className="mb-2"><i className="fas fa-check text-primary me-2" style={{ color: "var(--color-primary)" }}></i> Mobile meter entry — enter opening and closing units in seconds.</li>
                        <li className="mb-2"><i className="fas fa-check text-primary me-2" style={{ color: "var(--color-primary)" }}></i> Fair tenant-day math so a resident staying 10 days never pays for 30.</li>
                        <li className="mb-2"><i className="fas fa-check text-primary me-2" style={{ color: "var(--color-primary)" }}></i> Automatic top-up recovery for late readings without losing revenue.</li>
                      </ul>
                    </div>
                  </div>

                  {/* Pillar 4 */}
                  <div className="col-lg-6">
                    <div className="pg-pillar-card">
                      <span className="pg-pillar-badge">Pillar 4: Resident Experience</span>
                      <h3 style={{ fontSize: "1.35rem", marginBottom: "14px", color: "#fff" }}>
                        <i className="fas fa-user-lock me-2" style={{ color: "var(--color-primary)" }}></i>
                        Resident Self-Service Portal & Inquiries
                      </h3>
                      <p className="opacity-80 mb-20" style={{ lineHeight: 1.7 }}>
                        Give residents a clean mobile-friendly portal to pay rent, view live mess menus, log maintenance complaints with photo attachments, and submit digital vacate notices.
                      </p>
                      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: "14px" }}>
                        <li className="mb-2"><i className="fas fa-check text-primary me-2" style={{ color: "var(--color-primary)" }}></i> WhatsApp bed booking requests & tenant inbox built right into your dashboard.</li>
                        <li className="mb-2"><i className="fas fa-check text-primary me-2" style={{ color: "var(--color-primary)" }}></i> Formal deposit ledger with transparent move-out deduction settlements.</li>
                        <li className="mb-2"><i className="fas fa-check text-primary me-2" style={{ color: "var(--color-primary)" }}></i> Photo maintenance tickets with SLA tracking and warden notifications.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* =========================================================================
                COMPARISON TABLE — Why AXOIX vs Single-Purpose Apps & Spreadsheets
               ========================================================================= */}
            <section className="pt-40 pb-60">
              <div className="container">
                <div className="text-center mb-40">
                  <span className="sub-title mb-15">Clear Honest Comparison</span>
                  <h2 className="title">Why PG Owners Switch to AXOIX</h2>
                  <p className="opacity-75 mt-15" style={{ maxWidth: 700, margin: "15px auto 0" }}>
                    Compare AXOIX against traditional paper diaries and single-purpose rent collection apps.
                  </p>
                </div>

                <div className="table-responsive" style={{ maxWidth: 940, margin: "0 auto" }}>
                  <table className="pg-compare-table">
                    <thead>
                      <tr>
                        <th>Operational Capability</th>
                        <th style={{ color: "var(--color-primary)" }}>AXOIX-iBOS PG</th>
                        <th>RentOk</th>
                        <th>Excel / Paper Diary</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Starter Pricing</strong></td>
                        <td><strong>Free Forever (up to 20 beds)</strong></td>
                        <td>Paid from Bed 1</td>
                        <td>Free (Manual Effort)</td>
                      </tr>
                      <tr>
                        <td><strong>Electricity Sub-Metering</strong></td>
                        <td><strong>Fair Tenant-Day Formula</strong></td>
                        <td>Basic / Flat</td>
                        <td>Complex Manual Math</td>
                      </tr>
                      <tr>
                        <td><strong>Deposit Settlement</strong></td>
                        <td><strong>Itemized Double-Entry Voucher</strong></td>
                        <td>Collection Only</td>
                        <td>Informal Note</td>
                      </tr>
                      <tr>
                        <td><strong>Bank Statement Match</strong></td>
                        <td><strong>Automated Passbook Reconcile</strong></td>
                        <td>Manual Review</td>
                        <td>Manual Cross-Check</td>
                      </tr>
                      <tr>
                        <td><strong>Business Back-Office</strong></td>
                        <td><strong>Built-in Staff HR, Payroll, GST</strong></td>
                        <td>None (PG Only)</td>
                        <td>Separate Software Needed</td>
                      </tr>
                      <tr>
                        <td><strong>Public Website & Storefront</strong></td>
                        <td><strong>Included with Live Bed Requests</strong></td>
                        <td>App listing only</td>
                        <td>None</td>
                      </tr>
                      <tr>
                        <td><strong>Multi-Vertical Capability</strong></td>
                        <td><strong>Run PG, Hotel & Cafe in 1 Login</strong></td>
                        <td>PG Only</td>
                        <td>Fragmented</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="text-center mt-30">
                  <Link to="/compare/rentok" className="thm-btn agency-btn" style={{ background: "transparent", border: "1px solid rgba(0,255,151,0.3)" }}>
                    <span className="text">Read the Full AXOIX vs RentOk Breakdown →</span>
                  </Link>
                </div>
              </div>
            </section>

            {/* =========================================================================
                ALL 15 OPERATIONAL FEATURES GRID
               ========================================================================= */}
            {featureTitles.length > 0 && (
              <section className="pt-60 pb-60">
                <div className="container">
                  <div className="text-center mb-50">
                    <span className="sub-title mb-15">{t("pgPms.featuresSubtitle")}</span>
                    <h2 className="title">{t("pgPms.featuresTitle")}</h2>
                  </div>
                  <div className="row">
                    {featureTitles.map((featureTitle, idx) => (
                      <div key={idx} className="col-lg-4 col-md-6 mb-20">
                        <div className="industry-detail-feature-card">
                          <div className="industry-detail-feature-icon">
                            <i className={`fas ${FEATURE_ICONS[idx] || "fa-check"}`}></i>
                          </div>
                          <h3 style={{ fontSize: "1.1rem", marginBottom: "8px" }}>{featureTitle}</h3>
                          <p className="mb-0 opacity-75" style={{ fontSize: "14px", lineHeight: 1.7 }}>
                            {featureDescs[idx]}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* =========================================================================
                PUBLIC STOREFRONT & WEBSITE BUILDER
               ========================================================================= */}
            <section className="pb-60">
              <div className="container">
                <div className="industry-detail-feature-card p-40">
                  <div className="row align-items-center">
                    <div className="col-lg-7">
                      <h3 className="mb-15" style={{ fontSize: "1.45rem" }}>
                        <i className="fas fa-globe me-2" style={{ color: "var(--color-primary)" }}></i>
                        Your Own Branded Booking Website Included
                      </h3>
                      <p className="opacity-75 mb-20" style={{ lineHeight: 1.7 }}>
                        Every subscription includes a ready-to-publish website with real-time bed availability. Prospective residents can filter by sharing type, air-conditioning, and move-in date to submit booking requests directly to your warden dashboard.
                      </p>
                      <div className="d-flex gap-2 flex-wrap">
                        {["Custom Domain Support", "Mobile Responsive", "Instant UPI QR Booking", "No Commission Fees"].map((tag, idx) => (
                          <span key={idx} className="industry-detail-capability-tag">{tag}</span>
                        ))}
                      </div>
                    </div>
                    <div className="col-lg-5 text-center mt-30 mt-lg-0">
                      <div style={{ fontSize: "72px", color: "var(--color-primary)", opacity: 0.35 }}>
                        <i className="fas fa-store"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* =========================================================================
                PLUS BUILT-IN BUSINESS BACK-OFFICE
               ========================================================================= */}
            <section className="pt-40 pb-60">
              <div className="container">
                <div className="text-center mb-40">
                  <h2 className="title" style={{ fontSize: "1.75rem" }}>Complete Business Operating System Included</h2>
                  <p className="opacity-75 mt-10" style={{ maxWidth: 650, margin: "10px auto 0" }}>
                    Run daily PG operations without buying separate software for accounting, GST filings, or staff payroll.
                  </p>
                </div>
                <div className="industry-detail-module-grid">
                  {CORE_MODULES.map((mod, idx) => (
                    <div key={idx} className="industry-detail-module-item">
                      <i className={`fas ${mod.icon}`}></i>
                      <span>{t(mod.nameKey)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* =========================================================================
                PRICING SECTION — Unified Theme Plan Cards
               ========================================================================= */}
            <PricingSection
              hideBg
              hideComparison
              hideAddons
              hideFaqHelp
              sectionId="pg-pricing"
              className="pricing pt-40 pb-60"
              verticalSlug="pg-hostel-management-software"
              customSubtitle="Simple Transparent Pricing"
              customTitle="Simple Pricing, Scaled for Your PG & Hostel"
              customDesc="Start free with 20 beds on the Explorer plan. Every plan runs the whole PG system on an isolated MySQL tenant database — you only pay for capacity as you scale."
            />

            {/* =========================================================================
                PLATFORM SPECIFICATIONS & TRANSPARENCY
               ========================================================================= */}
            {gaps.length > 0 && (
              <section className="pt-20 pb-40">
                <div className="container">
                  <div
                    className="pricing-item xb-border bg_img p-40"
                    style={{
                      backgroundImage: `url(${cardBg})`,
                      backgroundSize: "cover",
                      backgroundColor: "#0b1222",
                    }}
                  >
                    <div className="d-flex align-items-center mb-25" style={{ gap: "16px" }}>
                      <div
                        style={{
                          width: "48px",
                          height: "48px",
                          borderRadius: "12px",
                          background: "rgba(0, 255, 151, 0.12)",
                          border: "1px solid rgba(0, 255, 151, 0.3)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "var(--color-primary)",
                          fontSize: "20px",
                          flexShrink: 0,
                        }}
                      >
                        <i className="fas fa-shield-halved"></i>
                      </div>
                      <div>
                        <h3 className="mb-1 text-white" style={{ fontSize: "1.35rem", fontWeight: 700 }}>
                          {t("pgPms.gapsTitle")}
                        </h3>
                        <p className="mb-0 text-white-50" style={{ fontSize: "0.95rem" }}>
                          {t("pgPms.gapsDesc")}
                        </p>
                      </div>
                    </div>
                    <div className="row g-3">
                      {gaps.map((gap, idx) => (
                        <div key={idx} className="col-md-6 d-flex align-items-start" style={{ gap: "12px" }}>
                          <i
                            className="fas fa-circle-check mt-1"
                            style={{ color: "var(--color-primary)", fontSize: "16px", flexShrink: 0 }}
                          ></i>
                          <span style={{ fontSize: "0.92rem", color: "#e2e8f0", lineHeight: 1.55 }}>
                            {gap}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* =========================================================================
                OPERATOR FAQ — Theme Native Accordion
               ========================================================================= */}
            {faqQuestions.length > 0 && (
              <section className="pt-40 pb-60" id="pg-faq">
                <div className="container">
                  <div className="sec-title sec-title--two pricing-sec-title text-center mb-50">
                    <span className="sub-title mb-25 wow fadeInUp" data-wow-duration="600ms">
                      <img src={subLeftIcon} alt="icon" /> FAQ’s <img src={subRightIcon} alt="icon" />
                    </span>
                    <h2 className="title wow fadeInUp" data-wow-delay="150ms" data-wow-duration="600ms">
                      {t("pgPms.faqTitle")}
                    </h2>
                  </div>
                  <div className="row justify-content-center">
                    <div className="col-lg-12">
                      <div className="xb-faq wow fadeInUp" data-wow-duration="600ms">
                        <ul className="accordion_box clearfix list-unstyled">
                          {faqQuestions.map((question, idx) => {
                            const isOpen = activeFaqId === idx;
                            const numStr = idx + 1 < 10 ? `0${idx + 1}` : `${idx + 1}`;
                            return (
                              <li
                                key={idx}
                                className={`accordion block ${isOpen ? "active-block" : ""}`}
                              >
                                <div
                                  className={`acc-btn ${isOpen ? "active" : ""}`}
                                  onClick={() => setActiveFaqId(isOpen ? null : idx)}
                                >
                                  <span className="number">{numStr}</span>
                                  _ {question}
                                  <span className="arrow">
                                    <span></span>
                                  </span>
                                </div>
                                <div
                                  className={`acc_body ${isOpen ? "current" : ""}`}
                                  style={{
                                    maxHeight: isOpen ? "500px" : "0px",
                                    overflow: "hidden",
                                    transition: "max-height 0.4s ease",
                                  }}
                                >
                                  <div className="content">
                                    <p>{faqAnswers[idx]}</p>
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* =========================================================================
                RELATED PG GUIDES & ARTICLES
               ========================================================================= */}
            <section className="pt-40 pb-60">
              <div className="container">
                <div className="text-center mb-40">
                  <h2 className="title">{t("pgPms.articlesTitle")}</h2>
                  <p className="opacity-75 mt-15">{t("pgPms.articlesDesc")}</p>
                </div>
                <div className="row">
                  {RELATED_ARTICLES.map((slug, idx) => (
                    <div key={slug} className="col-lg-4 col-md-6 mb-20">
                      <Link to={`/blog/${slug}`} className="pg-article-link">
                        <i className="fas fa-file-lines me-2" style={{ color: "var(--color-primary)" }}></i>
                        {articleTitles[idx] || slug}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* =========================================================================
                BOTTOM HIGH-URGENCY CTA SECTION
               ========================================================================= */}
            <section className="pt-60 pb-100">
              <div className="container">
                <div className="text-center pricing-item xb-border bg_img p-50 industry-detail-cta-section">
                  <div className="pg-badge-pill mb-20">
                    <i className="fas fa-lock-open"></i>
                    <span>Free Forever for 20 Beds • Instant Setup</span>
                  </div>
                  <h2 className="mb-15" style={{ fontSize: "2.2rem" }}>
                    {t("pgPms.ctaTitle")}
                  </h2>
                  <p className="opacity-75 mb-30" style={{ maxWidth: "620px", margin: "0 auto 30px", fontSize: "1.05rem", lineHeight: 1.7 }}>
                    {t("pgPms.ctaDesc")}
                  </p>
                  <div className="pg-hero-btn-group justify-content-center">
                    <Link
                      to="/signup?vertical=pg&from=pg"
                      className="pg-cta-btn pg-cta-btn-primary"
                    >
                      {t("pgPms.ctaPrimary")}
                    </Link>
                    {hasDemos && (
                      <Link
                        to="/demo/pg"
                        className="pg-cta-btn pg-cta-btn-demo"
                      >
                        <i className="fas fa-play-circle me-1"></i>
                        {t("pgPms.ctaDemo")}
                      </Link>
                    )}
                    <a
                      href="#pg-pricing"
                      className="pg-cta-btn pg-cta-btn-plans"
                    >
                      {t("pgPms.ctaPricing")}
                    </a>
                  </div>
                  <p className="opacity-60 mt-25 mb-0" style={{ fontSize: "13px", maxWidth: "640px", margin: "25px auto 0" }}>
                    {t("pgPms.ctaNote")}
                  </p>
                </div>
              </div>
            </section>

          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default PGPage;
